/* Copyright (c) 2020-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import { When } from "react-if";
import slugify from "slugify";
import styled from "styled-components";
import { Button, Table, theme, FlexContainer } from "@nordcloud/gnui";
import { SearchInput } from "~/components/SearchInput/SearchInput";
import { Sidebar } from "~/components/Sidebar";
import { useSleep } from "~/hooks";
import { isNotEmpty, isEmpty } from "~/tools";
import { Item } from "~/types";
import { LoaderWrap } from "../Loader";
import { SidebarItemType } from "./SidebarItem";
import { SidebarItemList } from "./SidebarItemList";
import { MultiSelectSubmitContainer, Wrap } from "./styles";
import { getMultiSelectLabel } from "./utils";

function filterOptions(options: readonly Item[], query: string) {
  const lowerCaseQuery = query.toLowerCase();
  return options.filter(
    ({ id, columns }) =>
      id?.toLowerCase().includes(lowerCaseQuery) ||
      columns?.some(({ data }) => {
        return data.some((element) => {
          return element.toLowerCase().includes(lowerCaseQuery);
        });
      })
  );
}
const StyledTable = styled(Table)`
  tbody {
    td {
      padding: ${theme.spacing.spacing03} ${theme.spacing.spacing02}
        ${theme.spacing.spacing03} ${theme.spacing.spacing02};
    }
    [aria-disabled="true"] {
      &:hover {
        cursor: no-drop;
      }
    }
  }
`;

type ButtonConfig = {
  label: string;
  onClick: () => void;
  order?: number;
  severity?: "high" | "low";
};

type Props = {
  options: Item[];
  name: string;
  pluralName?: string;
  title?: string;
  tableHeadline?: string;
  showValueInOptions?: boolean;
  onClose: () => void;
  isOpen: boolean;
  placeholder?: string;
  onItemSelect: (id: string) => void;
  onSubmit: (ids: string[]) => void;
  selectedOptions: string[];
  disabledOptions?: { id: string; state: boolean }[];
  sidebarItem?: SidebarItemType;
  loading: boolean;
  customButtonsConfig?: ButtonConfig[];
  onClear?: () => void;
  serverSideSearch?: boolean;
  renderPagination?: React.ReactNode;
  onSearchChange?: (phrase: string) => void;
  noDataContent?: React.ReactNode;
};

export function SidebarSelector({
  options,
  name = "",
  pluralName,
  title,
  tableHeadline,
  showValueInOptions,
  isOpen,
  onClose,
  onItemSelect,
  onSubmit,
  selectedOptions,
  disabledOptions = [],
  sidebarItem,
  loading,
  customButtonsConfig,
  onClear,
  serverSideSearch,
  renderPagination,
  onSearchChange,
  noDataContent,
}: Props) {
  const [query, setQuery] = useState("");
  const [searchInputKey, setSearchInputKey] = useState(0);
  const { sleep, cancelSleep } = useSleep();

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setSearchInputKey((k) => k + 1);
    }
  }, [isOpen]);

  const selectedCount = selectedOptions.length;

  function handleClose() {
    setQuery("");
    onClose();
  }

  function handleSubmit() {
    setQuery("");
    onSubmit(selectedOptions);
  }

  function handleClear() {
    setQuery("");
    onClear?.();
  }

  const getDataTestId = (id: string) => {
    return `button-select-${slugify(id, {
      lower: true,
    })}`;
  };

  const defaultButtons: ButtonConfig[] = [
    {
      label: "Apply",
      onClick: handleSubmit,
      severity: "high",
    },
    {
      label: "Clear",
      onClick: handleClear,
      severity: "low",
    },
  ];

  const buttons = customButtonsConfig?.length
    ? customButtonsConfig.reduce(
        (acc: ButtonConfig[], customButton, currentIndex) => {
          const position =
            customButton.order ?? defaultButtons.length + currentIndex;

          return [
            ...acc.slice(0, position),
            {
              label: customButton.label,
              onClick: customButton.onClick,
            },
            ...acc.slice(position),
          ];
        },
        [...defaultButtons]
      )
    : defaultButtons;

  return (
    <Sidebar
      title={title || `Select ${name}`}
      isOpen={isOpen}
      onClick={handleClose}
      headerStyles={{
        zIndex: 3,
      }}
      footer={
        <FlexContainer
          direction="column"
          css={{
            width: "100%",
            alignItems: "flex-start",
            paddingTop: `${theme.spacing.spacing03}`,
          }}
        >
          <span
            css={{
              paddingLeft: `${theme.spacing.spacing04}`,
              fontSize: `${theme.fontSizes.sm}`,
              color: `${theme.color.text.text02}`,
            }}
          >
            {getMultiSelectLabel(selectedCount, name, pluralName)}
          </span>
          <MultiSelectSubmitContainer>
            {buttons.map((buttonCfg: ButtonConfig) => (
              <Button
                key={buttonCfg.label}
                data-testid={getDataTestId(buttonCfg.label)}
                onClick={buttonCfg.onClick}
                disabled={selectedCount === 0}
                severity={buttonCfg.severity ?? "high"}
                style={{ textTransform: "none" }}
              >
                {buttonCfg.label}
              </Button>
            ))}
          </MultiSelectSubmitContainer>
        </FlexContainer>
      }
    >
      <Wrap>
        <When condition={isNotEmpty(options) || serverSideSearch}>
          <SearchInput
            key={String(searchInputKey)}
            placeholder="Search"
            onChange={(val: string) => {
              setQuery(val);
              if (serverSideSearch) {
                cancelSleep();
                sleep(600).then(() => {
                  onSearchChange?.(val);
                  return null;
                });
              }
            }}
            onSubmit={(val: string) => {
              setQuery(val);
              if (serverSideSearch) {
                onSearchChange?.(val);
              }
            }}
          />
        </When>
      </Wrap>
      <LoaderWrap loading={loading} inContent>
        <When condition={isEmpty(options)}>{noDataContent ?? "No Data."}</When>
        <When condition={isNotEmpty(options)}>
          <StyledTable hoverline striped>
            <Table.thead>
              <Table.tr>
                <Table.th width="1rem" />
                <Table.th>{tableHeadline}</Table.th>
              </Table.tr>
            </Table.thead>
            <SidebarItemList
              items={serverSideSearch ? options : filterOptions(options, query)}
              sidebarItem={sidebarItem}
              onSelect={onItemSelect}
              {...{ showValueInOptions, selectedOptions, disabledOptions }}
            />
          </StyledTable>
          {renderPagination}
        </When>
      </LoaderWrap>
    </Sidebar>
  );
}
